import { useEffect, useState } from 'react';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';
import { api, ApiKeyRecord, CreatedApiKey, GameInfoAuth } from '../api';

interface Props {
  session: Session;
  games: GameInfoAuth[];
  onGameUpdated: (game: { game_id: string; name: string; enabled_packages: string[] }) => void;
}

type Tab = 'general' | 'api-keys' | 'billing';

export default function GameSettings({ session, games, onGameUpdated }: Props) {
  const [tab, setTab] = useState<Tab>('general');
  const game = games.find(g => g.game_id === session.gameId);

  return (
    <Container>
      <PageHeader
        title="Game Settings"
        subtitle={game ? game.name : session.gameId}
      />
      <div className="flex gap-1 border-b border-line mb-4">
        <TabButton active={tab === 'general'} onClick={() => setTab('general')}>
          General
        </TabButton>
        <TabButton active={tab === 'api-keys'} onClick={() => setTab('api-keys')}>
          API Keys
        </TabButton>
        <TabButton active={tab === 'billing'} onClick={() => setTab('billing')}>
          Billing
        </TabButton>
      </div>

      {tab === 'general' && <General session={session} game={game} onGameUpdated={onGameUpdated} />}
      {tab === 'api-keys' && <ApiKeysTab session={session} />}
      {tab === 'billing' && <BillingTab />}
    </Container>
  );
}

function TabButton({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      className={[
        'px-3 py-2 text-sm font-medium border-b-2 -mb-px',
        active ? 'border-fg text-fg' : 'border-transparent text-fg-muted hover:text-fg',
      ].join(' ')}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function General({
  session, game, onGameUpdated,
}: {
  session: Session;
  game: GameInfoAuth | undefined;
  onGameUpdated: Props['onGameUpdated'];
}) {
  const [name, setName] = useState(game?.name ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => { setName(game?.name ?? ''); }, [game?.name]);

  const save = async () => {
    if (!name.trim() || name.trim() === game?.name) return;
    setBusy(true); setError(null);
    try {
      const updated = await api.updateGame(session.gameId, { name: name.trim() });
      onGameUpdated({
        game_id: updated.game_id,
        name: updated.name,
        enabled_packages: updated.enabled_packages,
      });
      setSavedAt(Date.now());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card max-w-2xl">
      {error && <div className="mb-3 text-red-500 text-sm">{error}</div>}
      <label className="block text-xs uppercase text-fg-muted font-semibold mb-1">Game ID</label>
      <code className="block text-xs bg-bg-2 border border-line rounded px-2 py-1.5 mb-4">
        {session.gameId}
      </code>

      <label className="block text-xs uppercase text-fg-muted font-semibold mb-1">Name</label>
      <div className="flex gap-2 mb-4">
        <input
          className="input flex-1"
          value={name}
          onChange={e => setName(e.target.value)}
          maxLength={120}
        />
        <button
          className="btn btn-primary"
          onClick={save}
          disabled={busy || !name.trim() || name.trim() === game?.name}
        >
          {busy ? 'Saving...' : 'Save'}
        </button>
      </div>
      {savedAt && (
        <div className="text-xs text-fg-muted mb-4">
          Saved {new Date(savedAt).toLocaleTimeString()}
        </div>
      )}

      <label className="block text-xs uppercase text-fg-muted font-semibold mb-1">Enabled packages</label>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {(game?.enabled_packages ?? []).map(p => (
          <span key={p} className="badge badge-info">{p}</span>
        ))}
        {(game?.enabled_packages ?? []).length === 0 && (
          <span className="text-fg-muted text-sm">None</span>
        )}
      </div>
    </div>
  );
}

function ApiKeysTab({ session }: { session: Session }) {
  const [keys, setKeys] = useState<ApiKeyRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [justCreated, setJustCreated] = useState<CreatedApiKey | null>(null);

  const reload = () => {
    api.listApiKeys(session.gameId)
      .then(res => setKeys(res.keys))
      .catch(e => setError((e as Error).message));
  };
  useEffect(reload, [session.gameId]);

  const create = async () => {
    if (!newName.trim()) return;
    setBusy(true); setError(null);
    try {
      const created = await api.createApiKey(session.gameId, { name: newName.trim() });
      setJustCreated(created);
      setNewName('');
      reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const revoke = async (keyId: string) => {
    if (!confirm('Revoke this API key? This cannot be undone.')) return;
    try {
      await api.revokeApiKey(session.gameId, keyId);
      reload();
    } catch (e) { setError((e as Error).message); }
  };

  return (
    <div>
      <div className="text-xs text-fg-muted mb-3">
        Server-to-server tokens scoped to this game. Used by your game server for runtime calls.
      </div>
      {error && <div className="card border-red-500/40 mb-4 text-red-500">{error}</div>}

      {justCreated && (
        <div className="card mb-4 border-green-500/40">
          <div className="font-medium mb-1">Key created — copy it now</div>
          <div className="text-xs text-fg-muted mb-2">Plaintext shown once.</div>
          <code className="block break-all bg-bg-2 border border-line rounded px-2 py-1.5 text-xs">
            {justCreated.token}
          </code>
          <button className="btn btn-ghost text-xs mt-2" onClick={() => setJustCreated(null)}>
            Dismiss
          </button>
        </div>
      )}

      <div className="card mb-4">
        <div className="font-medium mb-2">Create new key</div>
        <div className="flex gap-2">
          <input
            className="input flex-1"
            placeholder="Key name (e.g. game-server-prod)"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            disabled={busy}
          />
          <button
            className="btn btn-primary"
            onClick={create}
            disabled={busy || !newName.trim()}
          >
            {busy ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="font-medium mb-3">Active keys</div>
        {keys.length === 0 ? (
          <div className="empty text-sm">No API keys yet.</div>
        ) : (
          <KeyTable
            rows={keys}
            onRevoke={revoke}
          />
        )}
      </div>
    </div>
  );
}

function BillingTab() {
  return (
    <div className="card max-w-2xl">
      <div className="font-medium mb-2">Billing</div>
      <div className="text-fg-muted text-sm">
        Billing setup is not yet wired up. Plan, payment method, and invoice history will appear here.
      </div>
    </div>
  );
}

export function KeyTable({
  rows, onRevoke,
}: {
  rows: { id: string; name: string; key_prefix: string; scopes: string[]; created_at: string; last_used_at: string | null; revoked_at: string | null }[];
  onRevoke: (id: string) => void;
}) {
  return (
    <div className="table-wrap">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-fg-muted text-xs uppercase">
            <th className="py-1.5">Name</th>
            <th className="py-1.5">Prefix</th>
            <th className="py-1.5">Scopes</th>
            <th className="py-1.5">Created</th>
            <th className="py-1.5">Last used</th>
            <th className="py-1.5">Status</th>
            <th className="py-1.5"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(k => (
            <tr key={k.id} className="border-t border-line">
              <td className="py-2">{k.name}</td>
              <td className="py-2"><code className="text-xs">{k.key_prefix}…</code></td>
              <td className="py-2 text-xs">{k.scopes.join(', ')}</td>
              <td className="py-2 text-xs">{new Date(k.created_at).toLocaleString()}</td>
              <td className="py-2 text-xs">
                {k.last_used_at ? new Date(k.last_used_at).toLocaleString() : '—'}
              </td>
              <td className="py-2">
                {k.revoked_at
                  ? <span className="badge badge-muted">revoked</span>
                  : <span className="badge badge-ok">active</span>}
              </td>
              <td className="py-2 text-right">
                {!k.revoked_at && (
                  <button className="btn btn-danger text-xs" onClick={() => onRevoke(k.id)}>
                    Revoke
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
